import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

// PrimeNG Modules
import { FileUploadModule, FileUploadHandlerEvent } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';

// Services and Models
import { PromoPlanService } from '../promo-plan.service';
import { ChatSession } from '../../models/chat-session.model';
import { ChatSessionService } from '../../services/chat-session.service';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { MAX_FILE_SIZE_BYTES } from '../../shared/constants/file-size.constants';
import { SPREADSHEET_UPLOAD_ACCEPT_TYPES } from '../../shared/constants/upload-file-types.constants';

@Component({
  selector: 'app-promo-plan-upload',
  standalone: true,
  imports: [
    CommonModule,
    FileUploadModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    MessagesModule,
    ProgressSpinnerModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './promo-plan-upload.component.html',
  styleUrls: ['./promo-plan-upload.component.css']
})
export class PromoPlanUploadComponent implements OnInit, OnDestroy {
  readonly maxFileSize = MAX_FILE_SIZE_BYTES;
  readonly spreadsheetAcceptTypes = SPREADSHEET_UPLOAD_ACCEPT_TYPES;

  templateColumns: { name: string; mandatory: string; validation: string }[] = [
    { name: 'Name', mandatory: 'Yes', validation: 'No' },
    { name: 'Channel', mandatory: 'Yes', validation: 'Yes' },
    { name: 'Customer Number', mandatory: 'Yes', validation: 'Yes' },
    { name: 'Customer Name', mandatory: 'No', validation: 'No' },
    { name: 'Brand', mandatory: 'Yes', validation: 'Yes' },
    { name: 'Sub-Brand', mandatory: 'No (only when the activity is visibility)', validation: 'Yes' },
    { name: 'Tamer Item No.', mandatory: 'No', validation: 'Yes If not null' },
    { name: 'Customer Item No.', mandatory: 'No', validation: 'No' },
    { name: 'Item Description', mandatory: 'No', validation: 'No' },
    { name: 'Month', mandatory: 'No', validation: 'No' },
    { name: 'Start Date', mandatory: 'Yes', validation: 'No' },
    { name: 'End Date', mandatory: 'Yes', validation: 'No' },
    { name: 'Promo Type', mandatory: 'Yes', validation: 'Yes' },
    { name: 'Execution Type', mandatory: 'Yes', validation: 'Yes' },
    { name: 'IMS', mandatory: 'Yes', validation: 'No' },
    { name: 'PTT', mandatory: 'Yes', validation: 'No' },
    { name: 'Promo Description', mandatory: 'Yes', validation: 'No' },
    { name: 'Discount %', mandatory: 'Yes', validation: 'Yes' },
    { name: 'Price After Discount', mandatory: 'Yes', validation: 'No (PTT-Disc Amount)' },
    { name: 'Discount Amount', mandatory: 'Yes', validation: 'No (Formula)' },
    { name: 'Estimated Volume', mandatory: 'No', validation: 'No' },
    { name: 'Total Compensation/Fixed Amount', mandatory: 'Yes Only when visibility', validation: 'No' },
    { name: 'Compensation Based on LC', mandatory: 'No Based on Brand', validation: 'No' },
    { name: 'Stock OH', mandatory: 'No', validation: 'No' },
    { name: 'Current RSP', mandatory: 'Yes Based on Customer', validation: 'No' },
    { name: 'Promo RRP', mandatory: 'No Based on Customer', validation: 'No' },
    { name: 'New Cost', mandatory: 'No', validation: 'No' },
    { name: 'Comments', mandatory: 'Yes', validation: 'No' }
  ];

  fileToUpload: File | null = null;
  isLoading: boolean = false;
  isProcessing: boolean = false;
  isUploadComplete: boolean = false;

  private routeSub!: Subscription;
  private sessionSub!: Subscription;
  currentSessionId: number | null = null;
  currentSession: ChatSession | undefined;


  constructor(
    private messageService: MessageService,
    private promoPlanService: PromoPlanService,
    private route: ActivatedRoute,
    private chatSessionService: ChatSessionService,
    private firebaseStorageService: FirebaseStorageService
  ) {}

  ngOnInit(): void {
    // The traversal logic is confirmed to be working. We can remove the verbose logging.
    let routeWithSessionId: ActivatedRoute | null = this.route;
    while (routeWithSessionId && !routeWithSessionId.snapshot.paramMap.has('sessionId')) {
      routeWithSessionId = routeWithSessionId.parent;
    }

    this.routeSub = (routeWithSessionId || this.route).paramMap.subscribe(params => {
      const sessionIdParam = params.get('sessionId');
      if (sessionIdParam) {
        this.currentSessionId = +sessionIdParam;
        this.sessionSub = this.chatSessionService.sessions$.subscribe(sessions => {
          this.currentSession = sessions.find(s => s.sessionId === this.currentSessionId);
        });
      } else {
        const errorMsg = 'Session ID not found in route parameters.';
        this.messageService.add({ severity: 'error', summary: 'Routing Error', detail: errorMsg });
        console.error(`[PromoPlanUploadComponent] ERROR: ${errorMsg}`);
      }
    });
  }

  async onFileSelect(event: FileUploadHandlerEvent | any): Promise<void> {
    this.isLoading = true;
    this.fileToUpload = null;
    this.isUploadComplete = false;

    const file = event.files[0];
    this.fileToUpload = file;
    this.isLoading = false;
    this.messageService.add({ severity: 'success', summary: 'File Selected', detail: `Ready to upload ${file.name}.` });
  }

  customUploadHandler(event: FileUploadHandlerEvent): void {
    this.onFileSelect(event);
  }

  sendPromoPlanToBackend(): void {
    if (!this.fileToUpload) {
      this.messageService.add({ severity: 'warn', summary: 'No File', detail: 'Please select an Excel file first.' });
      return;
    }
    if (!this.currentSessionId) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Session ID is missing. Cannot send data.' });
      return;
    }

    this.isProcessing = true;
    const filePath = `promo-plans/${this.currentSessionId}/${this.fileToUpload.name}`;

    this.firebaseStorageService.uploadFile(this.fileToUpload, filePath).subscribe({
      next: (downloadURL) => {
        this.messageService.add({ severity: 'info', summary: 'Upload Started', detail: 'File uploaded to cloud storage. Sending to backend for processing...' });
        console.log('File uploaded successfully to Firebase Storage:', downloadURL);
        this.promoPlanService.uploadPromoPlan(this.currentSessionId!, downloadURL).subscribe({
          next: (response: string) => {
            this.isProcessing = false;
            this.isUploadComplete = true;
            this.messageService.add({ severity: 'success', summary: 'Upload Successful', detail: 'Promo plan sent for processing.' });
            console.log('Promo plan uploaded successfully', response);
            this.fileToUpload = null;
          },
          error: (err) => {
            this.isProcessing = false;
            this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not send promo plan to backend.' });
            console.error('Error uploading promo plan', err);
          }
        });
      },
      error: (err) => {
        this.isProcessing = false;
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload file to cloud storage.' });
        console.error('Error uploading file to storage', err);
      }
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.sessionSub?.unsubscribe();
  }
}
